import { useCallback, useEffect, useId, useState } from 'react';
import type { Database } from '@/lib/database.types';
import { supabase } from '@/lib/supabase';
import { Card } from '@/shared/components/ui/Card';
import { useI18n } from '@/shared/i18n/useT';
import { isAdmin, useOrg } from '@/shared/org';
import { CreateOrganizationCard } from './CreateOrganizationCard';
import { OrgAddressCard } from './OrgAddressCard';

type BookingMode = 'guest' | 'account' | 'both';
type PublicLocale = 'en' | 'es' | 'pt';

const BOOKING_MODES: readonly BookingMode[] = ['both', 'account', 'guest'];
const LOCALES: readonly PublicLocale[] = ['en', 'es', 'pt'];

/**
 * The organization's own settings: its name, how clients may book, and which
 * languages the public booking page offers.
 *
 * All three hang off the organization rather than a profile — the booking
 * policy belongs to the business, not to whichever staff member happens to be
 * configuring it. Non-admins see the values read-only rather than a hidden
 * section: knowing the policy is useful even when changing it is not yours.
 */
export function OrganizationSection() {
  const { t } = useI18n();
  const org = useOrg();
  const nameId = useId();

  const [name, setName] = useState('');
  const [bookingMode, setBookingMode] = useState<BookingMode>('both');
  const [locales, setLocales] = useState<readonly PublicLocale[]>(['es']);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const orgId = org.status === 'ready' ? org.orgId : null;
  const editable = org.status === 'ready' && !org.isViewingAs && isAdmin(org.role);

  useEffect(() => {
    if (!orgId) return;
    let active = true;

    void (async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('name, booking_mode, public_locales')
        .eq('id', orgId)
        .maybeSingle();

      if (!active) return;
      if (!error && data) {
        setName(data.name);
        setBookingMode(data.booking_mode as BookingMode);
        setLocales((data.public_locales ?? ['es']) as PublicLocale[]);
      }
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [orgId]);

  const save = useCallback(
    async (patch: Database['public']['Tables']['organizations']['Update']) => {
      if (!orgId) return;
      setStatus('saving');
      const { error } = await supabase.from('organizations').update(patch).eq('id', orgId);
      setStatus(error ? 'error' : 'saved');
    },
    [orgId],
  );

  function toggleLocale(locale: PublicLocale) {
    // At least one language has to remain: an empty list would render a booking
    // page nobody can read, and the check constraint refuses it anyway.
    const next = locales.includes(locale)
      ? locales.filter((item) => item !== locale)
      : [...locales, locale];
    if (next.length === 0) return;

    setLocales(next);
    void save({ public_locales: next });
  }

  if (org.status !== 'ready' || loading) {
    return <p className="text-sm text-muted">{t('common.loading')}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <Card title={t('settings.org.name.title')} description={t('settings.org.name.description')}>
        <label htmlFor={nameId} className="sr-only">
          {t('settings.org.name.title')}
        </label>
        <input
          id={nameId}
          value={name}
          disabled={!editable}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => editable && name.trim() !== '' && void save({ name: name.trim() })}
          className="tap-target w-full max-w-sm rounded-full border border-border-strong bg-surface px-3 text-sm text-fg disabled:opacity-60"
        />
      </Card>

      <Card
        title={t('settings.org.bookingMode.title')}
        description={t('settings.org.bookingMode.description')}
      >
        <div className="flex flex-col gap-2">
          {BOOKING_MODES.map((mode) => (
            <label key={mode} className="flex items-start gap-3 text-sm">
              <input
                type="radio"
                name="booking-mode"
                value={mode}
                checked={bookingMode === mode}
                disabled={!editable}
                onChange={() => {
                  setBookingMode(mode);
                  void save({ booking_mode: mode });
                }}
                className="mt-1 rounded-full"
              />
              <span>
                <span className="font-medium text-fg">
                  {t(`settings.org.bookingMode.${mode}`)}
                </span>
                <span className="block text-muted">
                  {t(`settings.org.bookingMode.${mode}Hint`)}
                </span>
              </span>
            </label>
          ))}
        </div>
      </Card>

      <Card
        title={t('settings.org.locales.title')}
        description={t('settings.org.locales.description')}
      >
        <div className="flex flex-wrap gap-3">
          {LOCALES.map((locale) => (
            <label key={locale} className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={locales.includes(locale)}
                disabled={!editable}
                onChange={() => toggleLocale(locale)}
               className="rounded-full" />
              {t(`language.${locale}`)}
            </label>
          ))}
        </div>
      </Card>

      <OrgAddressCard />

      {status === 'error' && (
        <p role="alert" className="text-sm text-danger">
          {t('settings.org.saveError')}
        </p>
      )}
      {status === 'saved' && <p className="text-sm text-muted">{t('settings.org.saved')}</p>}
      {!editable && <p className="text-sm text-muted">{t('settings.org.readOnly')}</p>}

      {/* Not gated on `editable`: creating a NEW organization is not an act of
          authority over this one. A viewer in their employer's tenant is still
          entitled to start their own. */}
      <CreateOrganizationCard />
    </div>
  );
}
